import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, unwrapObject } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, CheckCircle2, Loader2, Pencil, Play, Plus, SkipForward, Trash2, Truck } from "lucide-react";
import { fmtDay, PRIORITIES, qty, type WorkOrderRecord, type WorkOrderStageRecord } from "@/types/production";
import { PriorityBadge, StageStatusBadge, WorkOrderStatusBadge } from "@/components/production/ProductionBadges";
import { MaterialSection } from "@/components/production/MaterialSection";
import { JobWorkSection } from "@/components/production/JobWorkSection";
import { OutputSection } from "@/components/production/OutputSection";
import { AddStageDialog, CompleteStageDialog, PlanStageDialog, ReasonDialog, StartStageDialog } from "@/components/production/StageDialogs";

const when = (d: string) => new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
const field = "w-full h-10 rounded-lg border border-input bg-background px-3 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring";

export function WorkOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { success, error } = useToast();
  const canUpdate = hasPermission("production.update");
  const canClose = hasPermission("production.close");
  const [w, setW] = useState<WorkOrderRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [start, setStart] = useState<WorkOrderStageRecord | null>(null);
  const [complete, setComplete] = useState<WorkOrderStageRecord | null>(null);
  const [plan, setPlan] = useState<WorkOrderStageRecord | null>(null);
  const [skip, setSkip] = useState<WorkOrderStageRecord | null>(null);
  const [send, setSend] = useState<WorkOrderStageRecord | null>(null);
  const [sub, setSub] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [edit, setEdit] = useState({ planned_qty: "", due_date: "", priority: "NORMAL", notes: "" });

  const load = useCallback(async () => {
    if (!id) return;
    setSub((k) => k + 1);
    try {
      setW(unwrapObject<WorkOrderRecord>(await api.get(`/api/WorkOrder/ById/${id}`)));
    } catch (err: any) {
      error("Failed to load work order", err.message || "Work order not found");
      navigate("/production");
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);

  if (loading || !w) return <Loader2 className="size-5 animate-spin text-muted-foreground m-6" />;
  const open = w.work_order_status === "PLANNED" || w.work_order_status === "IN_PROGRESS";
  const stages = w.stages || [];
  const currentId = stages.find((s) => s.stage_status === "PENDING" || s.stage_status === "IN_PROGRESS")?.id;

  const openEdit = () => {
    setEdit({ planned_qty: String(Number(w.planned_qty)), due_date: w.due_date || "", priority: w.priority, notes: w.notes || "" });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    try {
      await api.put(`/api/WorkOrder/Update/${w.id}`, { planned_qty: Number(edit.planned_qty), due_date: edit.due_date || null, priority: edit.priority, notes: edit.notes });
      success("Work order updated", "");
      setEditOpen(false);
      load();
    } catch (err: any) {
      error("Could not update", err.message || "Please try again");
    }
  };
  const remove = async (s: WorkOrderStageRecord) => {
    try {
      await api.delete(`/api/WorkOrder/StageRemove/${w.id}/${s.id}`);
      load();
    } catch (err: any) {
      error("Could not remove", err.message || "Please try again");
    }
  };
  const close = async () => {
    try {
      await api.patch(`/api/WorkOrder/Close/${w.id}`);
      success("Work order closed", "");
      load();
    } catch (err: any) {
      error("Could not close", err.message || "Please try again");
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <Link to="/production" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-1">
          <ArrowLeft className="size-3" /> Production
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2 flex-wrap">
              {w.wo_number} <WorkOrderStatusBadge status={w.work_order_status} /> <PriorityBadge priority={w.priority} />
              {w.late && <span className="text-xs font-semibold text-rose-600 border border-rose-300 rounded-full px-2 py-0.5">Running late</span>}
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {w.product_name}
              {w.variant_name ? ` · ${w.variant_name}` : ""} · {qty(w.planned_qty, w.unit)}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {canUpdate && open && (
              <>
                <Button size="sm" variant="outline" className="h-9 gap-1.5" onClick={openEdit}>
                  <Pencil className="size-3.5" /> Edit
                </Button>
                <Button size="sm" variant="outline" className="h-9 text-red-600" onClick={() => setCancelOpen(true)}>
                  Cancel work order
                </Button>
              </>
            )}
            {canClose && w.work_order_status === "COMPLETED" && (
              <Button size="sm" className="h-9 gap-1.5" onClick={close}>
                <CheckCircle2 className="size-4" /> Close work order
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
        <div className="rounded-xl border bg-card p-3.5">
          <p className="text-[11px] uppercase font-semibold tracking-wider text-muted-foreground">For</p>
          <p className="font-semibold mt-1">{w.order ? <Link to={`/orders/${w.order.id}`} className="text-primary hover:underline">{w.order.order_number}</Link> : "Stock"}</p>
          {w.account && <p className="text-xs text-muted-foreground">{w.account.name}</p>}
        </div>
        <div className="rounded-xl border bg-card p-3.5">
          <p className="text-[11px] uppercase font-semibold tracking-wider text-muted-foreground">Due</p>
          <p className={`font-semibold mt-1 ${w.late ? "text-rose-600" : ""}`}>{fmtDay(w.due_date)}</p>
          <p className="text-xs text-muted-foreground">Plan finishes {fmtDay(w.planned_finish)}</p>
        </div>
        <div className="rounded-xl border bg-card p-3.5">
          <p className="text-[11px] uppercase font-semibold tracking-wider text-muted-foreground">Produced</p>
          <p className="font-semibold mt-1">{w.produced_qty != null ? qty(w.produced_qty, w.unit) : "-"}</p>
          <p className="text-xs text-muted-foreground">{w.yield_pct != null ? `Yield ${w.yield_pct}%` : "Not finished"}{w.wastage_total ? ` · ${qty(w.wastage_total, w.unit)} lost` : ""}</p>
        </div>
        <div className="rounded-xl border bg-card p-3.5">
          <p className="text-[11px] uppercase font-semibold tracking-wider text-muted-foreground">Owner</p>
          <p className="font-semibold mt-1">{w.owner ? `${w.owner.first_name} ${w.owner.last_name || ""}`.trim() : "-"}</p>
          <p className="text-xs text-muted-foreground">Told if a stage runs late</p>
        </div>
      </div>
      {w.notes && <div className="rounded-xl border bg-card px-4 py-3 text-sm">{w.notes}</div>}
      {w.cancelled_reason && <div className="rounded-xl border border-rose-300/60 bg-rose-500/5 px-4 py-3 text-sm">Cancelled: {w.cancelled_reason}</div>}

      <div className="bg-card border rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="text-sm font-bold">Stages</h3>
          {canUpdate && open && (
            <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => setAddOpen(true)}>
              <Plus className="size-3.5" /> Add stage
            </Button>
          )}
        </div>
        <ol className="divide-y">
          {stages.map((s, i) => (
            <li key={s.id} className={`px-4 py-3 flex flex-col lg:flex-row lg:items-center gap-3 ${s.id === currentId && open ? "bg-primary/5" : ""}`}>
              <div className="flex items-start gap-3 lg:w-[34%]">
                <span className="mt-0.5 size-6 shrink-0 rounded-full border border-border bg-background text-xs font-semibold flex items-center justify-center">{i + 1}</span>
                <div>
                  <div className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                    {s.process_name} <StageStatusBadge status={s.stage_status} />
                    {s.overdue && <span className="text-[11px] font-semibold text-rose-600">Late</span>}
                  </div>
                  <div className="text-[11px] text-muted-foreground">{s.execution === "JOB_WORK" ? `Job work · ${s.supplier?.name || "no job worker yet"}` : "In-house"}</div>
                </div>
              </div>
              <div className="text-xs text-muted-foreground lg:w-[22%]">
                <div>Plan {fmtDay(s.planned_start)} → {fmtDay(s.planned_end)}</div>
                {(s.actual_start || s.actual_end) && <div>Actual {fmtDay(s.actual_start)} → {fmtDay(s.actual_end)}</div>}
              </div>
              <div className="text-xs lg:w-[24%]">
                {s.input_qty != null || s.output_qty != null ? (
                  <div>
                    <span className="text-muted-foreground">In </span>{qty(s.input_qty)} <span className="text-muted-foreground">· out </span><b>{qty(s.output_qty)}</b>
                    {Number(s.wastage_qty) > 0 && <span className="text-rose-600"> · lost {qty(s.wastage_qty)}</span>}
                  </div>
                ) : (
                  <span className="text-muted-foreground">{s.remarks || ""}</span>
                )}
                {s.remarks && (s.input_qty != null || s.output_qty != null) && <div className="text-muted-foreground">{s.remarks}</div>}
              </div>
              <div className="flex gap-1.5 flex-wrap lg:ml-auto">
                {canUpdate && open && s.stage_status === "PENDING" && (
                  <>
                    <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => setStart(s)}>
                      <Play className="size-3.5" /> Start
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setPlan(s)}>Plan</Button>
                    <Button size="sm" variant="ghost" className="h-8 text-xs gap-1" onClick={() => setSkip(s)}>
                      <SkipForward className="size-3.5" /> Skip
                    </Button>
                    <Button size="icon" variant="ghost" className="size-8 text-muted-foreground" title="Remove stage" onClick={() => remove(s)}>
                      <Trash2 className="size-3.5" />
                    </Button>
                  </>
                )}
                {canUpdate && open && s.stage_status === "IN_PROGRESS" && (
                  <>
                    {s.execution === "JOB_WORK" && (
                      <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => setSend(s)}>
                        <Truck className="size-3.5" /> Send material
                      </Button>
                    )}
                    <Button size="sm" className="h-8 text-xs gap-1" onClick={() => setComplete(s)}>
                      <CheckCircle2 className="size-3.5" /> Complete
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setPlan(s)}>Plan</Button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ol>
      </div>

      <MaterialSection w={w} canUpdate={canUpdate} reloadKey={sub} onChanged={load} />
      <JobWorkSection w={w} canUpdate={canUpdate} reloadKey={sub} onChanged={load} sendStage={send} onSendClose={() => setSend(null)} />
      <OutputSection w={w} canUpdate={canUpdate} reloadKey={sub} onChanged={load} />

      <div className="bg-card border rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b">
          <h3 className="text-sm font-bold">History</h3>
        </div>
        <ul className="divide-y text-sm">
          {[...(w.history || [])].reverse().map((h) => (
            <li key={h.id} className="px-4 py-2.5 flex items-start justify-between gap-3">
              <div>
                <div>{h.comment || h.action}</div>
                {h.to_status && h.from_status !== h.to_status && <div className="text-[11px] text-muted-foreground">{h.from_status ? `${h.from_status} → ` : ""}{h.to_status}</div>}
              </div>
              <div className="text-[11px] text-muted-foreground whitespace-nowrap text-right">
                {when(h.created_on)}
                {h.by && <div>{h.by}</div>}
              </div>
            </li>
          ))}
        </ul>
      </div>

      <StartStageDialog workOrder={w} stage={start} onClose={() => setStart(null)} onSaved={load} />
      <CompleteStageDialog workOrder={w} stage={complete} onClose={() => setComplete(null)} onSaved={load} />
      <PlanStageDialog workOrder={w} stage={plan} onClose={() => setPlan(null)} onSaved={load} />
      <AddStageDialog workOrder={w} open={addOpen} onClose={() => setAddOpen(false)} onSaved={load} />
      <ReasonDialog
        open={!!skip}
        title={`Skip ${skip?.process_name}`}
        description="The stage is left out of this work order. Say why."
        confirmLabel="Skip stage"
        onClose={() => setSkip(null)}
        onConfirm={async (reason) => {
          await api.post(`/api/WorkOrder/StageSkip/${w.id}/${skip!.id}`, { reason });
          load();
        }}
      />
      <ReasonDialog
        open={cancelOpen}
        title={`Cancel ${w.wo_number}`}
        description="Nothing more can be done on a cancelled work order."
        confirmLabel="Cancel work order"
        destructive
        onClose={() => setCancelOpen(false)}
        onConfirm={async (reason) => {
          await api.patch(`/api/WorkOrder/Cancel/${w.id}`, { reason });
          load();
        }}
      />
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit {w.wo_number}</DialogTitle>
            <DialogDescription>Quantity, due date and priority of an open work order.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Quantity ({w.unit})</Label>
                <Input inputMode="decimal" value={edit.planned_qty} onChange={(e) => setEdit({ ...edit, planned_qty: e.target.value })} className="h-10" />
              </div>
              <div className="space-y-1.5">
                <Label>Due by</Label>
                <Input type="date" value={edit.due_date} onChange={(e) => setEdit({ ...edit, due_date: e.target.value })} className="h-10" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Priority</Label>
              <select className={field} value={edit.priority} onChange={(e) => setEdit({ ...edit, priority: e.target.value })}>
                {Object.entries(PRIORITIES).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} className="h-10" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveEdit}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
